import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { 
  FolderOpen,
  Plus,
  ChevronDown,
  LogOut,
  ChevronUp,
  Coins
} from 'lucide-react';
import { authService } from '@/services/auth';
import { apiService } from '@/services/api';
import { Progress } from '@/components/ui/progress';

interface Project {
  id: number;
  name: string;
  description?: string;
  created_at: string;
}

const TopHeader: React.FC = () => {
  const userData = authService.getUserData();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const toggleProjectMenu = () => {
    setShowProjectMenu(!showProjectMenu);
  };

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      try {
        // Replace with actual API endpoint for projects
        const response = await apiService.get('/projects/');
        const projectData = response.data.results || response.data || [];
        setProjects(projectData);
        if (projectData.length > 0) {
          setCurrentProject(projectData[0]); // Set first project as current
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        // Mock data for development
        setProjects([
          { id: 1, name: 'Default Project', description: 'Main project', created_at: new Date().toISOString() }
        ]);
        setCurrentProject({ id: 1, name: 'Default Project', description: 'Main project', created_at: new Date().toISOString() });
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  const handleProjectSwitch = (project: Project) => {
    setCurrentProject(project);
    setShowProjectMenu(false);
    // Here you would typically update the global state or context
    // to reflect the project change across the app
  };

  const handleAddProject = () => {
    // Here you would typically open a modal or navigate to project creation
    console.log('Add new project');
    setShowProjectMenu(false);
  };

  return (
    <div className="p-4">
      <Card className="h-16 flex items-center justify-between px-6">
        {/* Left Side - Projects */}
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center space-x-2 hover:bg-muted/50"
              onClick={toggleProjectMenu}
            >
              <FolderOpen className="h-4 w-4" />
              <span className="text-sm font-medium">
                {isLoadingProjects ? 'Loading...' : currentProject?.name || 'No Project'}
              </span>
              <ChevronDown className="h-3 w-3" />
            </Button>
            
          {/* Project Dropdown */}
          {showProjectMenu && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-lg z-50">
              <div className="p-2 space-y-1">
                {Array.isArray(projects) && projects.map((project) => (
                  <Button
                    key={project.id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start p-2 text-xs"
                    onClick={() => handleProjectSwitch(project)}
                  >
                    <FolderOpen className="h-3 w-3 mr-2" />
                    <span className="truncate">{project.name}</span>
                  </Button>
                ))}
                <div className="border-t border-border mt-2 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start p-2 text-xs text-primary hover:bg-primary/10"
                    onClick={handleAddProject}
                  >
                    <Plus className="h-3 w-3 mr-2" />
                    Add New Project
                  </Button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Right Side - Token Display and User */}
        <div className="flex items-center space-x-6">
          {/* Token Display - Row Layout */}
          <div className="flex items-center space-x-3 min-w-[300px]">
            <div className="flex items-center space-x-2">
              <Coins className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Tokens</span>
            </div>
            <div className="w-1/3">
              <Progress 
                value={75} 
                className="h-2"
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              1,500/2,000
            </span>
          </div>
          
          {/* User Menu */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center space-x-2 hover:bg-muted/50"
              onClick={toggleUserMenu}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src="/avatars/01.png" alt="@user" />
                <AvatarFallback className="text-xs">
                  {userData?.name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="text-left">
                <div className="text-sm font-medium">
                  {userData?.name || 'User'}
                </div>
              </div>
              {showUserMenu ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
            
            {/* User Dropdown */}
            {showUserMenu && (
              <div className="absolute top-full right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-50">
                <div className="p-2">
                  <div className="px-2 py-1 mb-2">
                    <div className="text-sm font-medium">
                      {userData?.name || 'User'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {userData?.email || 'user@example.com'}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start p-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TopHeader;